import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useMyCompanies } from '@/api/companies';
import { getActiveCompanyId, setActiveCompanyId } from '@/lib/active-company';
import { LoadingSpinner } from '@/components/shared/loading-spinner';
import type { MyCompany } from '@/types';

type ActiveCompanyValue = {
  /** Effective active company id: stored choice if valid, else the primary. */
  activeCompanyId: number | null;
  activeCompany: MyCompany | undefined;
  companies: MyCompany[];
  isMultiCompany: boolean;
  setActiveCompany: (id: number) => void;
};

const ActiveCompanyContext = createContext<ActiveCompanyValue | undefined>(undefined);

// Loads the collaborator's companies, resolves the active one (localStorage
// choice validated against memberships, else primary), and gates children
// behind a spinner so no scoped request fires with an unresolved company.
export function ActiveCompanyProvider({ children }: { children: ReactNode }) {
  const { data: companies = [], isLoading } = useMyCompanies();
  const queryClient = useQueryClient();
  const [storedId, setStoredId] = useState<number | null>(() => getActiveCompanyId());

  // Self-heal: drop a stale stored id from localStorage (external system) once
  // companies load, so the API client stops sending a 403-causing header. The
  // active company is derived below, so no React state update is needed here.
  useEffect(() => {
    if (isLoading) return;
    if (storedId !== null && !companies.some((c) => c.company_id === storedId)) {
      setActiveCompanyId(null);
    }
  }, [isLoading, companies, storedId]);

  if (isLoading) return <LoadingSpinner className="min-h-screen" />;

  const primary = companies.find((c) => c.is_primary) ?? companies[0];
  const chosen = storedId !== null ? companies.find((c) => c.company_id === storedId) : undefined;
  const activeCompany = chosen ?? primary;

  const setActiveCompany = (id: number) => {
    setActiveCompanyId(id);
    setStoredId(id);
    void queryClient.invalidateQueries();
  };

  const value: ActiveCompanyValue = {
    activeCompanyId: activeCompany?.company_id ?? null,
    activeCompany,
    companies,
    isMultiCompany: companies.length > 1,
    setActiveCompany,
  };

  return <ActiveCompanyContext.Provider value={value}>{children}</ActiveCompanyContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- context hook colocated with its provider
export function useActiveCompany(): ActiveCompanyValue {
  const ctx = useContext(ActiveCompanyContext);
  if (!ctx) {
    throw new Error('useActiveCompany debe usarse dentro de ActiveCompanyProvider');
  }
  return ctx;
}
